import styled from 'styled-components';
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Autocomplete from "@mui/material/Autocomplete";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Table from "@mui/material/Table";

export const StyledTable = styled(Table)`
  table-layout: fixed;
  width: 100%;
`;

export const StyledTableContainer = styled(TableContainer).withConfig({
  shouldForwardProp: (prop: string) => !['tableContainerSx'].includes(prop),
}) <{ tableContainerSx?: any }>`
  box-shadow: none;
  border-radius: 6px;
  table-layout: fixed;
  width: 100%;
  ${({ tableContainerSx }) => tableContainerSx && Object.entries(tableContainerSx).map(([key, value]) => `${key}: ${value};`).join('\n')}
`;

export const StyledEntriesAutocomplete = styled(Autocomplete)`
  width: 5rem;
`;

export const StyledGridContainer = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'breakpointBreached',
}) <{ breakpointBreached: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ breakpointBreached }) => breakpointBreached ? '1fr' : '2fr 1fr'};
  grid-gap: 1rem;
  justify-content: space-between;
`;

export const StyledSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
`;



export const StyledCalendarIcon = styled(FontAwesomeIcon)`
  margin-right: 10px;
`;

export const StyledUserIcon = styled(FontAwesomeIcon)`
  margin-right: 10px;
  height: 1.2rem;
`;

export const StyledFallbackContainer = styled.div`
  width: 100%;
  height: 8rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  & > * {
    margin: 0.5rem 0;
  }
`;

export const StyledEmptyContainer = styled.div`
  height: 30vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const StyledPaginationContainer = styled.div.withConfig({
  shouldForwardProp: (prop: string) => !['showTotalEntries', 'totalPages'].includes(prop),
}) <{
  showTotalEntries: boolean;
  totalPages: number;
}>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${({ showTotalEntries, totalPages }) =>
    !showTotalEntries && totalPages <= 1 ? '0' : '24px'};
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
export const StyledEntriesInfo = styled.div`
  margin-bottom: 0;
  
  @media (max-width: 600px) {
    margin-bottom: 24px;
  }
`;

export const StyledEntriesText = styled(Typography)`
  text-transform: unset;
`;

export const StyledPaginationInputContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 5rem;
  margin: 0 8px;
`;

export const StyledChevronIcon = styled(Icon)`
  font-weight: bold;
`;

export const StyledHeadCellContainer = styled.th.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'width',
}) <{ width?: string | number }>`
  width: ${({ width }) => width || 'auto'};
  min-width: ${({ width }) => width || 'auto'};
  max-width: ${({ width }) => width || 'none'};
  padding: 12px 24px;
  border-bottom: 1px solid #225E9F80;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
`;

export const StyledHeadCellContent = styled.div.withConfig({
  shouldForwardProp: (prop: string) => !['align', 'sorted', 'onClick'].includes(prop),
}) <{
  align: 'left' | 'right' | 'center';
  sorted?: 'none' | 'asce' | 'desc' | false;
  onClick?: () => void;
}>`
  position: relative;
  text-align: ${({ align }) => align};
  opacity: 1;
  font-size: 1.1rem;
  font-weight: 700;
  text-transform: capitalize;
  cursor: 'pointer';
  color: #1C2650;
  padding: 8px 8px 8px 0;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: ${({ align }) => align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start'};
  min-height: 24px;
`;

export const StyledSortIconContainer = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'align',
}) <{
  align: 'left' | 'right' | 'center';
}>`
  position: relative;
  margin-left: 8px;
  margin-top: 3px;
  font-size: 1.125rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

export const StyledSortArrow = styled.div.withConfig({
  shouldForwardProp: (prop: string) => !['isActive', 'direction'].includes(prop),
}) <{
  isActive: boolean;
  direction: 'up' | 'down';
}>`
  position: relative;
  line-height: 1;
  opacity: ${({ isActive }) => isActive ? 1 : 0.5};
  color: ${({ theme, isActive }) =>
    isActive ? (theme.palette?.text?.primary || '#000') : (theme.palette?.text?.secondary || '#666')};
  margin: -8px 0;
`;

export const StyledBodyCellContainer = styled.td.withConfig({
  shouldForwardProp: (prop: string) => !['align', 'noBorder', 'verticalAlign'].includes(prop),
}) <{
  align?: 'left' | 'center' | 'right';
  noBorder?: boolean;
  verticalAlign?: 'top' | 'middle';
}>`
  text-align: ${({ align }) => align};
  padding: 12px 24px;
  font-size: 0.875rem;
  border-bottom: ${({ noBorder }) =>
    noBorder ? 'none' : '1px solid #225E9F80'};
  vertical-align: ${({ verticalAlign }) => verticalAlign};
  white-space: nowrap;
  overflow: hidden;
`;

export const StyledBodyCellContent = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'verticalAlign',
}) <{ verticalAlign?: 'top' | 'middle' }>`
  display: inline-block;
  width: max-content;
  vertical-align: ${({ verticalAlign }) => verticalAlign};
  color: ${({ theme }) => theme.palette?.text?.primary || '#000'};
`;

export const StyledDataTableRow = styled(TableRow).withConfig({
  shouldForwardProp: (prop: string) => !['isHovered', 'isClickable', 'hoverColor'].includes(prop),
}) <{
  isHovered: boolean;
  isClickable: boolean;
  hoverColor: string;
}>`
  background-color: ${({ isHovered, hoverColor }) => isHovered ? hoverColor : 'transparent'};
  cursor: ${({ isClickable }) => isClickable ? 'pointer' : 'default'};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${({ hoverColor }) => hoverColor};
  }
`;

export const StyledFlexContainer = styled.div`
  justify-content: space-between;
  align-items: center;
`;

export const StyledFlexAlignContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const StyledPaddingContainer = styled.div`
  padding-top: 8px;
  padding-bottom: 24px;
`; 